import { useContext, useEffect } from 'react'

import { useQuery } from '@apollo/client'
import { debounce, throttle } from 'lodash-es'

// import { useLocation } from 'react-router'
import notifier from 'tools/Notify'
import { toArray } from 'utils/array'

import { R_SIGNON, getAccessToken } from 'app/Auth/lib'
import { LOAD_USER } from 'libreon/User/graphql'
import GlobalContext, { R_GLOBAL } from 'reducer/global'

export function windowTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

export function pageTheme() {
  return isThemeDark() ? 'dark' : 'light'
}

export function isThemeDark() {
  return document.getElementById('theme').classList.contains('theme-dark')
}

function asTheme(str) {
  if (str[0] !== 't') {
    return 'theme-' + str
  }
  return str
}

export function useTheme() {
  const [state, dispatch] = useContext(GlobalContext)

  useEffect(() => {
    if (window.matchMedia) {
      if (window.matchMedia('(prefers-color-scheme: light)').matches) {
        dispatch({ type: R_GLOBAL.PAGE_THEME, value: 'theme-light' })
      }
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', (e) => {
          dispatch({
            type: R_GLOBAL.PAGE_THEME,
            value: asTheme(e.matches ? 'dark' : 'light')
          })
        })
    }
  }, [dispatch])

  const theme = state.user.settings.theme || state.pageTheme || windowTheme()

  useEffect(() => {
    document.getElementById('theme').classList.remove('theme-dark')
    document.getElementById('theme').classList.remove('theme-light')
    document.getElementById('theme').classList.add(asTheme(theme))
  }, [theme])
}

////////////////////////////////////////////////////////////////////////////////
export function useSignon() {
  const [, notifyDispatch] = useContext(GlobalContext)
  const [state, dispatch] = useContext(GlobalContext)

  // initialize apollo
  useEffect(() => {
    dispatch({ type: R_GLOBAL.APOLLO_RESET, dispatch })
  }, [dispatch])

  // Load user, refetch when we are able to do so (have a validation token)
  const { reloadUser, refreshToken, error: signon_error } = state.signon
  useEffect(() => {
    if (refreshToken) {
      getAccessToken(dispatch)
    }
  }, [refreshToken, dispatch])

  useQuery(LOAD_USER, {
    variables: { id: state.signon.sub, reloadUser },
    skip: !state.signon.sub,
    fetchPolicy: 'network-only',
    onCompleted({ users }) {
      if (users.success) {
        dispatch({ type: R_GLOBAL.USER_SET, value: users.result })
      } else {
        notifier.error(dispatch, users.reason)
      }
    }
  })

  // convert signon errors to notify
  useEffect(() => {
    if (signon_error) {
      notifier.error(notifyDispatch, signon_error)
      dispatch({ type: R_SIGNON.ERROR_CLEAR })
    }
  }, [signon_error, dispatch, notifyDispatch])
}

export function useOnResizeOrScroll(fxn, opts = {}) {
  const { type = 'debounce', wait = 200 } = opts
  useEffect(() => {
    /*
     * use whenever the page is resized or scrolled and we need to recalculate something
     * The bounding box is critical for mapping the screen mouse coordinates to svg element coordinates
     */
    const onPageChange =
      type === 'debounce' ? debounce(fxn, wait) : throttle(fxn, wait)

    onPageChange()

    window.addEventListener('scroll', onPageChange)
    window.addEventListener('resize', onPageChange)

    return () => {
      window.removeEventListener('scroll', onPageChange)
      window.removeEventListener('resize', onPageChange)
    }
  }, [fxn, type, wait])
}

export function useOnClickOutside(parentRefs, onClickOutside) {
  useEffect(() => {
    const onClick = ({ target }) => {
      const parents = toArray(parentRefs).map((p) => p.current)
      if (parents.some((p) => p == null)) return null
      if (parents.every((p) => p.contains(target) === false)) {
        onClickOutside(target)
      }
    }
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('click', onClick)
    }
  }, [parentRefs, onClickOutside])

  return null
}
